import {
  Box,
  Link as ChakraLink,
  Container,
  Flex,
  IconButton,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import {
  BuildingIcon,
  ChevronRightIcon,
  DealIcon,
  DynamicIconName,
  EmptyStateBox,
  ExclamationMarkInHexagonIcon,
  HandShakeIcon,
  InfoBox,
  InvoiceTextIcon,
  ItemWrapper,
  MovingTruckIcon,
  OrderPopup,
  ReturnBoxIcon,
  Spinner,
  ThreeBoxesIcon,
} from '@wanda-space/noelle'
import introductionSvg from '@wanda-space/noelle/illustrations/introduction.svg'
import {
  type OrderResponseWithItems,
  OrderState,
  OrderType,
  StorageItemState,
  itemCanBeInDeliveryOrder,
  itemHasPendingServiceOrder,
} from '@wanda-space/types'
import type { FeatureFlagKey } from 'api-client/lib/routes/feature-flags'
import { ItemImage } from 'components/Item/Image/ItemImage'
import { Routes } from 'consts'
import { useDateLocale } from 'contexts/Intl'
import { differenceInCalendarDays, format } from 'date-fns'
import { useDocumentTitle } from 'hooks'
import { useAppSelector } from 'hooks/useAppSelector'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useOrders } from 'hooks/useOrders'
import { useStorageFlowRouteSelector } from 'hooks/useStorageFlowRouteSelector'
import { eqProps, uniqWith } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { getServiceOrderString } from 'routes/Settings/Payments/Orders/utils'
import { hasOrderlineSquareMeterOrderline } from 'routes/SquareMeterFlow/helper'
import { pathTo } from 'utils'
import { sanitizeStripeAmount } from 'utils/price-utils'

import { useItems } from 'hooks/useItems'
import { ItemsTabs } from './Item/ItemsTabs'
import { StorageUnitSubscription } from './StorageUnitSubscription'

const Space = () => {
  const { formatMessage } = useIntl()
  useDocumentTitle(formatMessage({ id: 'word.space' }))
  const user = useAppSelector((state) => state.user.user)
  const locale = useDateLocale()
  const navigate = useNavigate()
  const { data: featureFlags } = useFeatureFlags({ suspense: true })

  const padding = 2
  const margin = 4
  const bigMargin = 10

  const { isLoading: itemsLoading, data: items } = useItems(user)

  const hasPriceIncrease = useMemo(
    () =>
      items?.some(
        (item) => item.newPrice && item.storageProduct.price !== item.newPrice?.product.price
      ) ?? false,
    [items]
  )

  const { data: orders } = useOrders()

  const linkToStorage = useStorageFlowRouteSelector()

  const titleIconList: {
    title: string
    icon: JSX.Element
    link: string
    featureFlag?: FeatureFlagKey
  }[] = [
    {
      title: formatMessage({ id: 'space.orderPopup.store' }),
      icon: <MovingTruckIcon />,
      link: linkToStorage.flowRoute,
      featureFlag: 'ENABLE_SPACESHIP_FLOW_STORAGE',
    },
    {
      title: formatMessage({ id: 'space.orderPopup.delivery' }),
      icon: <ReturnBoxIcon />,
      link: Routes.ReturnFlowSelectItems,
      featureFlag: 'ENABLE_SPACESHIP_FLOW_RETURN',
    },
    {
      title: formatMessage({ id: 'space.orderPopup.packaging' }),
      icon: <ThreeBoxesIcon />,
      link: Routes.Shop,
      featureFlag: 'ENABLE_SPACESHIP_FLOW_PACKAGING_IN_MENUS',
    },
  ]

  /* stored items */
  const storedItems = useMemo(() => {
    return (items || []).filter(({ state }) => state === StorageItemState.STORED)
  }, [items])

  /* booked items */
  const bookedItems = useMemo(() => {
    // @ts-ignore fix ItemResponseDto for pendingOrderId
    return (items || []).filter(({ pendingOrderId }) => pendingOrderId)
  }, [items])

  /* service items */
  const serviceItems = useMemo(() => {
    return (items || []).filter((item) => itemHasPendingServiceOrder(item))
  }, [items])

  /* not-stored items (but not if they are booked, then they are active) */
  const inactiveItems = useMemo(() => {
    return (items || []).filter(
      // @ts-ignore fix ItemResponseDto
      (item) => !itemCanBeInDeliveryOrder(item)
    )
  }, [items])

  /* items without images */
  const hasItemsWithoutImages = (items || []).some(({ image }) => !image)

  /* Upcoming orders */
  const upcomingOrders = useMemo(() => {
    return (orders || []).filter((order) => order.state === OrderState.PAYMENT_AUTHORISED)
  }, [orders])

  /* storage price */
  const amountOfStoredItems =
    items?.filter(({ state }) => state === StorageItemState.STORED).length || 0

  const totalStoragePrice = sanitizeStripeAmount(
    (items?.filter(({ state }) => state === StorageItemState.STORED) || [])
      .map(({ storageProduct }) => storageProduct?.price ?? 0)
      .reduce((a, b) => a + b, 0)
  )

  const activeItems = uniqWith(eqProps('id'), [...bookedItems, ...storedItems, ...serviceItems])

  if (itemsLoading) {
    return (
      <Flex width="100%" justifyContent="center">
        <Spinner />
      </Flex>
    )
  }

  return (
    <Container>
      <>
        <Box>
          <Text fontSize="xx-large" mb={margin}>
            {formatMessage({ id: 'space.greeting' }, { name: user?.address?.firstName })}
          </Text>
        </Box>

        <Flex mb={margin}>
          <OrderPopup
            title={formatMessage({ id: 'space.newOrder.popup.title' })}
            titleIconList={titleIconList.filter(
              ({ featureFlag }) => !featureFlag || featureFlags?.[featureFlag]
            )}
            buttonText={formatMessage({ id: 'space.newOrder.buttonText' })}
          >
            {''}
          </OrderPopup>
        </Flex>

        {/* Popup-section */}
        <Flex
          direction="column"
          mb={upcomingOrders.length || hasItemsWithoutImages ? bigMargin : ''}
          gap={4}
        >
          {hasPriceIncrease && (
            <InfoBox
              leftIcon={<InvoiceTextIcon />}
              onClick={() => navigate(Routes.PriceIncrease)}
              rightButton={
                <IconButton
                  as="span"
                  aria-label="icon-button"
                  backgroundColor="black"
                  onClick={() => navigate(Routes.PriceIncrease)}
                  icon={<ChevronRightIcon width={6} height={6} color="white" />}
                  size="xs"
                />
              }
              title={formatMessage({
                id: 'space.info-box.price-increase.title',
              })}
              description={
                <Text>
                  <Text as="span">
                    {formatMessage({
                      id: 'space.info-box.price-increase.description',
                    })}
                  </Text>{' '}
                  <Link style={{ textDecoration: 'underline' }} to={Routes.PriceIncrease}>
                    {formatMessage({
                      id: 'space.info-box.price-increase.link',
                    })}
                  </Link>
                </Text>
              }
            />
          )}
          {/* Active order popup */}
          {upcomingOrders.map((order) => (
            <UpcomingOrderInfoBox key={order.id} order={order} locale={locale} />
          ))}
          {/* No images popup */}
          {hasItemsWithoutImages && (
            <InfoBox
              leftIcon={<ExclamationMarkInHexagonIcon p="0.8" />}
              title={formatMessage({ id: 'space.infoBox.missingImages.title' })}
              onClick={() =>
                navigate(Routes.NoImageItemView, {
                  state: { referrer: location.pathname },
                })
              }
              description={
                <Flex direction="column">
                  <Text>
                    {formatMessage({
                      id: 'space.infoBox.missingImages.description',
                    })}
                  </Text>
                  <ChakraLink
                    style={{ textDecoration: 'underline' }}
                    onClick={() =>
                      navigate(Routes.NoImageItemView, {
                        state: { referrer: location.pathname },
                      })
                    }
                  >
                    {formatMessage({ id: 'space.infoBox.missingImages.link' })}
                  </ChakraLink>
                </Flex>
              }
            />
          )}
        </Flex>
        <StorageUnitSubscription mb={bigMargin} />
        {/* Stored and booked active items */}
        <Text mt={margin} mb={padding} fontSize="x-large">
          {formatMessage({ id: 'space.yourThings' }, { amountOfItems: amountOfStoredItems })}
        </Text>
        <Text mb={padding}>
          {formatMessage(
            { id: 'space.yourItemsSummary' },
            {
              amountOfStoredItems,
              priceOfStoredItems: totalStoragePrice,
            }
          )}
        </Text>
        <Box mb={bigMargin}>
          <ItemsTabs
            activeItems={activeItems}
            inactiveItems={inactiveItems}
            activeItemsEmptyStateBoxProps={{
              linkTo: Routes.SelectFlow,
            }}
          />
        </Box>
      </>
    </Container>
  )
}

function UpcomingOrderInfoBox({
  order,
  locale,
}: {
  order: OrderResponseWithItems
  locale: Locale | undefined
}) {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  function handleClickOrderDetails(id: string) {
    navigate(Routes.EditOrderDetails(id))
  }
  switch (order.type) {
    case OrderType.DELIVERY:
      return (
        <InfoBox
          leftIcon={<MovingTruckIcon />}
          onClick={() => handleClickOrderDetails(order.id)}
          rightButton={
            <IconButton
              as="span"
              aria-label="icon-button"
              backgroundColor="black"
              onClick={() => handleClickOrderDetails(order.id)}
              icon={<ChevronRightIcon width={6} height={6} color="white" />}
              size="xs"
            />
          }
          title={formatMessage(
            { id: 'space.infoBox.upcomingOrder.title' },
            {
              date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                locale: locale,
              }),
              from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
              to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
            }
          )}
          description={
            <>
              <Text mr="1">
                {formatMessage(
                  {
                    id: hasOrderlineSquareMeterOrderline(order.orderLines)
                      ? 'space.infoBox.upcomingOrder.description.m2.pickup'
                      : 'space.infoBox.upcomingOrder.description.pickup',
                  },
                  { amountOfItems: order.storageItems.length }
                )}
              </Text>
              <Link style={{ textDecoration: 'underline' }} to={Routes.EditOrderDetails(order.id)}>
                {formatMessage({ id: 'word.details' })}
              </Link>
            </>
          }
        />
      )

    case OrderType.PICK_UP:
      return (
        <InfoBox
          leftIcon={<MovingTruckIcon />}
          onClick={() => handleClickOrderDetails(order.id)}
          rightButton={
            <IconButton
              as="span"
              aria-label="icon-button"
              backgroundColor="black"
              onClick={() => handleClickOrderDetails(order.id)}
              icon={<ChevronRightIcon width={6} height={6} color="white" />}
              size="xs"
            />
          }
          title={formatMessage(
            { id: 'space.infoBox.upcomingOrder.title' },
            {
              date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                locale: locale,
              }),
              from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
              to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
            }
          )}
          description={
            <>
              <Text mr="1">
                {formatMessage(
                  { id: 'space.infoBox.upcomingOrder.description.delivery' },
                  { amountOfItems: order.storageItems.length }
                )}
              </Text>
              <Link style={{ textDecoration: 'underline' }} to={Routes.EditOrderDetails(order.id)}>
                {formatMessage({ id: 'word.details' })}
              </Link>
            </>
          }
        />
      )

    case OrderType.CUSTOMER_COLLECTS:
      return (
        <InfoBox
          leftIcon={<BuildingIcon />}
          onClick={() => handleClickOrderDetails(order.id)}
          title={formatMessage(
            { id: 'space.infoBox.upcomingCustomerCollects.title' },
            {
              date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                locale: locale,
              }),
              from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
              to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
            }
          )}
          description={
            <>
              {formatMessage(
                { id: 'space.infoBox.upcomingCustomerCollects.description' },
                {
                  date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                    locale: locale,
                  }),
                  from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
                  to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
                }
              )}{' '}
              <Link style={{ textDecoration: 'underline' }} to={Routes.EditOrderDetails(order.id)}>
                {formatMessage({ id: 'word.details' })}
              </Link>
            </>
          }
        />
      )

    case OrderType.CUSTOMER_DROP_OFF:
      return (
        <InfoBox
          leftIcon={<BuildingIcon />}
          onClick={() => handleClickOrderDetails(order.id)}
          title={formatMessage(
            { id: 'space.infoBox.upcomingCustomerDropOff.title' },
            {
              date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                locale: locale,
              }),
              from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
              to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
            }
          )}
          description={
            <>
              {formatMessage(
                { id: 'space.infoBox.upcomingCustomerCollects.description' },
                {
                  date: format(new Date(order.orderDetails.deliveryDate), 'EEEE dd.LLL ', {
                    locale: locale,
                  }),
                  from: order.orderDetails.deliveryTimeSlot.from.substring(0, 2),
                  to: order.orderDetails.deliveryTimeSlot.to.substring(0, 2),
                }
              )}{' '}
              <Link style={{ textDecoration: 'underline' }} to={Routes.EditOrderDetails(order.id)}>
                {formatMessage({ id: 'word.details' })}
              </Link>
            </>
          }
        />
      )
    default:
      return null
  }
}

export { Space }
